
import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spin, message, Button, Modal, Tag, Select, Input, } from "antd";
import { useNavigate, Link } from "react-router-dom";
import { EditOutlined, SearchOutlined, VerticalAlignTopOutlined } from "@ant-design/icons";
import axios from "axios";
import { DIFFICULTY_LEVELS, STORY_CATEGORIES, getCategory, } from "../config/storyCategories";

const { Meta } = Card;

const getDifficulty = (level) =>
  DIFFICULTY_LEVELS.find(
    (item) => item.value === Number(level)
  ) || DIFFICULTY_LEVELS[2];

const StoriesGallery = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState("All");
  const [selectedDifficulty, setSelectedDifficulty] = useState("All");
  const [searchTitle, setSearchTitle] = useState("");
  const [previewStory, setPreviewStory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");



  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 250);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const categories = [
    "All",
    ...Object.keys(STORY_CATEGORIES),
  ];

  // Load published stories.
  useEffect(() => {
    const fetchStories = async () => {
      setLoading(true);
      try {
        const res = await axios.get("/api/stories");
        setStories(
          res.data.sort((a, b) => b.id - a.id)
        );
      } catch (error) {
        console.error("Error loading stories:", error);
        message.error("Fehler beim Laden der Geschichten.");
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  // Apply category, difficulty and title-search filters.
  const filteredStories = stories.filter((story) => {
    const matchesCategory = selectedCat === "All" || story.cat?.toLowerCase() === selectedCat.toLowerCase();
    const matchesDifficulty = selectedDifficulty === "All" || Number(story.difficulty_level ?? 3) === Number(selectedDifficulty);
    const matchesTitle = (story.title || "")
      .toLowerCase()
      .includes(searchTitle.trim().toLowerCase());
    return (matchesCategory && matchesDifficulty && matchesTitle);
  });

  if (loading) {
    return <Spin size="large" className="loader" />;
  }

  return (
    <div className="stories-gallery">



      {/* GALLERY FILTERS */}
      {!currentUser ? (
        /* CATEGORY BUTTONS FOR UNAUTHENTICATED USERS */
        <div className="category-filter">
          {categories.map((cat) => {
            const mapped = STORY_CATEGORIES[cat];

            return (
              <Button
                key={cat}
                onClick={() => setSelectedCat(cat)}
                className={`btn-category ${selectedCat === cat ? "active" : ""
                  }`}
              >
                {cat === "All"
                  ? "🌈 Alle"
                  : `${mapped?.emoji || "📖"} ${mapped?.label || cat}`}
              </Button>
            );
          })}
        </div>
      ) : (
        /* FULL FILTERS FOR AUTHENTICATED USERS */
        <div className="gallery-filters">
          <Select
            className="gallery-filter-control gallery-category-select"
            value={selectedCat}
            onChange={setSelectedCat}
            options={[
              {
                value: "All",
                label: "📚 Alle Kategorien",
              },
              ...Object.keys(STORY_CATEGORIES).map((cat) => {
                const mapped = STORY_CATEGORIES[cat];

                return {
                  value: cat,
                  label: `${mapped?.emoji || "📖"} ${mapped?.label || cat
                    }`,
                };
              }),
            ]}
            showSearch
            optionFilterProp="label"
            placeholder="Kategorie auswählen"
          />

          <Select
            className="gallery-filter-control gallery-difficulty-select"
            value={selectedDifficulty}
            onChange={setSelectedDifficulty}
            options={[
              {
                value: "All",
                label: "Alle Lesestufen",
              },
              ...DIFFICULTY_LEVELS.map((level) => ({
                value: level.value,
                label: `${level.emoji} ${level.label}`,
              })),
            ]}
            placeholder="Lesestufe auswählen"
          />

          <Input
            className="gallery-filter-control gallery-search"
            placeholder="Titel suchen ..."
            prefix={<SearchOutlined />}
            allowClear
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
          />
        </div>
      )}







      {/* STORIES GRID */}
      <Row gutter={[16, 16]}>
        {filteredStories.map((story) => {
          const imageUrl = story.img
            ? `/api/stories/imagefile/${story.img}`
            : null;

          const cat = getCategory(story.cat);
          const difficulty = getDifficulty(
            story.difficulty_level
          );

          const plainDescription = (story.desc || "")
            .replace(/<[^>]+>/g, "");

          return (
            <Col
              xs={24}
              sm={12}
              md={8}
              lg={6}
              key={story.id}
            >
              <div className="book-wrapper">
                {/* CATEGORY BADGE */}
                <div className="category-badge">
                  {cat.emoji}
                </div>

                <div className="story-difficulty-badge"> {difficulty.emoji} {difficulty.label} </div>

                <Card
                  className="book-card"
                  hoverable
                  onClick={() => {
                    const user = JSON.parse(
                      localStorage.getItem("user") ||
                      "null"
                    );

                    if (!user) {
                      setPreviewStory(story);
                      setIsModalOpen(true);
                      return;
                    }

                    navigate("/StoryReader", {
                      state: {
                        storyId: story.id,
                        title: story.title,
                        desc: story.desc,
                        cat: story.cat,
                        img: imageUrl,
                        difficulty_level:
                          story.difficulty_level ?? 3,
                      },
                    });
                  }}
                  cover={
                    imageUrl ? (
                      <div className="book-cover">
                        <img
                          alt={story.title}
                          src={imageUrl}
                        />
                      </div>
                    ) : null
                  }
                >
                  {/* EDIT BUTTON FOR ADMIN */}
                  {currentUser?.role === 0 && (
                    <Link
                      to="/storyEditor"
                      state={{
                        storyId: story.id,
                        mode: "edit",
                      }}
                      onClick={(e) =>
                        e.stopPropagation()
                      }
                      className="edit-btn"
                    >
                      <EditOutlined />
                    </Link>
                  )}

                  <Meta
                    title={
                      <span className="book-title">
                        {story.title}
                      </span>
                    }
                    description={
                      <div className="book-desc">
                        <p>
                          {plainDescription.length > 160
                            ? `${plainDescription.slice(
                              0,
                              160
                            )}...`
                            : plainDescription}
                        </p>
                      </div>
                    }
                  />
                </Card>
              </div>
            </Col>
          );
        })}
      </Row>

      {/* EMPTY SEARCH/FILTER RESULTS */}
      {filteredStories.length === 0 && (
        <div className="empty-stories">
          Keine Geschichten für diese Suche oder
          Filter gefunden.
        </div>
      )}

      {/* STORY PREVIEW FOR GUESTS */}
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
      >
        {previewStory && (
          <div>
            <h2>{previewStory.title}</h2>

            <Tag color="purple">
              {
                getDifficulty(
                  previewStory.difficulty_level
                ).emoji
              }{" "}
              {
                getDifficulty(
                  previewStory.difficulty_level
                ).label
              }
            </Tag>

            {previewStory.img && (
              <img
                src={`/api/stories/imagefile/${previewStory.img}`}
                alt={previewStory.title}
                style={{
                  width: "100%",
                  borderRadius: 12,
                  marginTop: 16,
                  marginBottom: 16,
                }}
              />
            )}

            <div className="story-preview-description">
              {(previewStory.desc || "")
                .replace(/<[^>]+>/g, "")
                .slice(0, 600)}
              {(previewStory.desc || "").replace(
                /<[^>]+>/g,
                ""
              ).length > 600
                ? "..."
                : ""}
            </div>
          </div>
        )}
      </Modal>

      {showBackToTop && (
        <div className="back-to-top-container back-to-top-fixed">
          <button
            type="button"
            className="back-to-top-button"
            onClick={() =>
              window.scrollTo({ top: 0, behavior: "smooth" })
            }
            aria-label="Zurück nach oben"
            title="Nach oben"
          >
            <VerticalAlignTopOutlined className="back-to-top-icon" />
            <span>Nach oben</span>
          </button>
        </div>
      )}


    </div>
  );
};

export default StoriesGallery;
